<template>
    <div
        class="w-full flex lg:items-center xxs:flex-col lg:flex-row py-3 cursor-pointer xxs:bg-white xxs:border-b-px xxs:border-interactive-9 xxs:drop-shadow-[0_2px_8px_rgba(100,100,100,0.21)] lg:drop-shadow-none box-border xxs:px-3 xxs:mt-2"
        :class="[isLast ? '' : 'border border-b-[1px] border-t-0 border-l-0 border-r-0 border-b-default-10']"
        :style="{ zIndex: total - index }"
    >
        <div class="w-4 h-4">
            <Checkbox
                class="flex justify-center items-center"
                :modelValue="isSelected"
                @update:modelValue="emit('action', 'select', props.document, props.index, $event)"
                :showLabel="false"
            />
        </div>
        <div class="flex flex-row items-center justify-center xxs:mt-3 lg:mt-0 xxs:flex-1 lg:max-w-[47%]">
            <component :is="documentIcon" :color="fullConfig.theme.colors.primary[1]" class="xxs:w-6 lg:w-5 ml-4.5 xxs:-ml-0.5 lg:ml-4 flex-shrink-0" />
            <div
                @click="handleAction('navigate')"
                class="ml-4.5 xxs:w-auto xxs:flex-1 pr-4 text-body-6 xxs:pl-2 lg:pl-4 box-border text-default-1 capitalize lg:overflow-hidden lg:whitespace-nowrap lg:text-ellipsis "
            >
                {{ document.documentable.title }}
            </div>
        </div>
        <div class="flex xxs:flex-col lg:flex-row lg:items-center xxs:mt-2 lg:mt-0 lg:w-[53%] lg:justify-end">
            <div class="xxs:mt-1 lg:mt-0 lg:w-[19%] overflow-hidden text-ellipsis whitespace-nowrap xxs:pr-1 lg:pr-4 box-border text-body-7 text-default-1">{{ size }}</div>
            <div class="xxs:mt-3 lg:mt-0 lg:w-[19%] xxs:pr-1 lg:pr-4 box-border text-body-7 text-default-1">
                {{ formattedDate(document.documentable.created_at) }}
            </div>
            <div class="lg:w-[15%] flex justify-end lg:items-center">
                <img
                    v-if="isAdmin"
                    @click="handleAction('share')"
                    src="@/assets/icons/share-dark-grey.svg"
                    alt="view"
                    class="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4 cursor-pointer"
                />
                <div v-if="SHOW_FAVORITE">
                    <img
                        @click="handleAction('favorite')"
                        v-if="!document.isLiked"
                        src="@/assets/icons/favorite-outline.svg"
                        alt="view"
                        class="w-4 h-4 cursor-pointer"
                    />
                    <img
                        v-else
                        @click="handleAction('favorite')"
                        src="@/assets/icons/favorite.svg"
                        alt="view"
                        class="w-4 h-4 cursor-pointer"
                    />
                </div>
                <Menu as="div" class="relative inline-block text-left" v-if="(!isAdmin && !isFolder) || isAdmin">
                    <div>
                        <MenuButton>
                            <img alt="Options" src="@/assets/icons/dots.svg" class="xxs:w-6 xxs:h-6 lg:w-4 lg:h-4 ml-6 cursor-pointer" />
                        </MenuButton>
                    </div>

                    <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="transform scale-95 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-75 ease-in"
                        leave-from-class="transform scale-100 opacity-100"
                        leave-to-class="transform scale-95 opacity-0"
                    >
                        <MenuItems
                            class="absolute right-[4px] mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none max-w-[100px] z-[9999]"
                        >
                            <div class="px-1 py-1">
                                <MenuItem v-slot="{ active }" v-if="isAdmin">
                                    <button
                                        @click="handleAction('share')"
                                        :class="[active ? `bg-primary-1 hover:${textColorClass}` : 'text-default-1',
                                        'group flex w-full items-center rounded-md px-2 py-2 text-body-6']">
                                        Share
                                    </button>
                                </MenuItem>
                                <MenuItem v-slot="{ active }" v-if="isAdmin">
                                    <button
                                        @click="handleAction('move')"
                                        :class="[active ? `bg-primary-1 hover:${textColorClass}` : 'text-default-1',
                                        'group flex w-full items-center rounded-md px-2 py-2 text-body-6']">
                                        Move
                                    </button>
                                </MenuItem>
                                <MenuItem v-slot="{ active }" v-if="SHOW_FAVORITE">
                                    <button
                                        @click="handleAction('favorite')"
                                        :class="[active ? `bg-primary-1 hover:${textColorClass}` : 'text-default-1',
                                        'group flex w-full items-center rounded-md px-2 py-2 text-body-6']">
                                        Favorite
                                    </button>
                                </MenuItem>
                                <MenuItem v-slot="{ active }" v-if="!isFolder">
                                    <button
                                        @click="handleAction('download')"
                                        :class="[active ? `bg-primary-1 hover:${textColorClass}` : 'text-default-1',
                                        'group flex w-full items-center rounded-md px-2 py-2 text-body-6']">
                                        Download
                                    </button>
                                </MenuItem>
                                <MenuItem v-slot="{ active }">
                                    <button
                                        v-if="isAdmin"
                                        @click="handleAction('delete')"
                                        :class="[active ? `bg-primary-1 hover:${textColorClass}` : 'text-default-1',
                                        'group flex w-full items-center rounded-md px-2 py-2 text-body-6']">
                                        Delete
                                    </button>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </Menu>
            </div>
        </div>
    </div>
</template>

<script setup>
import FolderIcon from '@/components/icons/small/Documents/Folder.vue';
import FileIcon from '@/components/icons/small/Documents/FileDoc.vue';
import PDFIcon from '@/components/icons/small/Documents/FilePDF.vue';
import ImageIcon from '@/components/icons/small/Documents/FileJPG.vue';

import Checkbox from '@/components/core/Checkbox.vue';
import {computed} from "vue";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from '@/../tailwind.config.js'
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/vue";
import {useTheme} from "@/stores/ThemeStore";
import {useAuth} from "@/stores/AuthStore";

const {isAdmin} = useAuth();

const SHOW_FAVORITE = false;

const { textColorClass } = useTheme();

const props = defineProps({
    document: {
        type: Object,
        required: true
    },
    selectedDocuments: {
        type: Object,
        required: true
    },
    isAllSelected: {
        type: Boolean,
        required: true
    },
    index: {
        type: Number,
        required: true
    },
    total: {
        type: Number,
        required: true
    }
});

const emit = defineEmits(['action']);

const icons = {
    folder: FolderIcon,
    file: FileIcon,
    pdf: PDFIcon,
    image: ImageIcon
}

const fullConfig = resolveConfig(tailwindConfig)

function formattedDate(dateString) {
    const dateObj = new Date(dateString);

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];

    const day = dateObj.getDate();
    const month = months[dateObj.getMonth()];
    const year = dateObj.getFullYear();

    return `${day} ${month}, ${year}`;
}

const documentIcon = computed(() => {
    if(props.document.documentable_type === 'folder') {
        return icons['folder'];
    }

    if(props.document.documentable.title.includes('.pdf')) {
        return icons['pdf'];
    }

    if(props.document.documentable.title.includes('.jpg')) {
        return icons['image'];
    }

    return icons['file'];
})

function formatFileSize(bytes) {
    if (bytes < 1024 * 1024) {
        // Convert to KB if size is less than 1 MB
        return (bytes / 1024).toFixed(2) + ' KB';
    } else {
        // Convert to MB if size is 1 MB or more
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    }
}

const size = computed(() => {
    if(props.document.documentable_type === 'folder') {
        return '-'
    }

    return formatFileSize(props.document.documentable.filesize);
})

const isLast = computed(() => {
    return props.index === props.total - 1;
})

const isSelected = computed(() => {
    return ((props.isAllSelected || props.selectedDocuments.include[0] === '*')  && !props.selectedDocuments.exclude.includes(props.document.uid)) || props.selectedDocuments.include.includes(props.document.uid);
})

const isFolder = computed(() => {
    return props.document.documentable_type === 'folder';
})

function handleAction(action) {
    emit('action', action, props.document, props.index);
}
</script>